/**
 * Pending role changes inside the Find People modal.
 *
 * The modal batches everything — assignee picks, roles added, roles removed —
 * and commits them in one "Save All". Until then nothing reaches the database or
 * the board, so Cancel really cancels. This module is the pure half of that: it
 * decides what counts as a change, which tab to focus, and what writes a save
 * turns into. It imports no React and no Supabase so it can be unit-tested
 * directly (see findPeoplePending.test.ts).
 *
 * Two id sets describe the pending structure:
 *  - `pendingAddIds`   roles created in this session; they exist only in the
 *                      modal's local `assignments` until save.
 *  - `pendingDeleteIds` saved roles queued for deletion; their tab stays visible
 *                      (struck through) so the pending state is legible.
 *
 * A locally-added role that's removed again leaves `assignments` outright, so the
 * two sets never overlap.
 */
import type { Assignment, AssignmentEmployee } from "@/hooks/useProjectBoardData";
import { withWrittenEmployees } from "@/utils/assignmentConfirmation";

/** Pending assignee picks, keyed by assignment id. Absent = unchanged. */
export type SelectionMap = Map<string, Set<string>>;

export interface PendingState {
  assignments: Assignment[];
  pendingAddIds: Set<string>;
  pendingDeleteIds: Set<string>;
  selections: SelectionMap;
}

export interface SavePlan {
  /** Roles to insert, in tab order. */
  inserts: Assignment[];
  /** Employee sets to write once the inserts land. */
  employeeWrites: Array<{
    assignment: Assignment;
    selectedIds: Set<string>;
    /** True when the role is being created in this same save: its INSERT audit
     *  already records the role, so the employee UPDATE entry would be noise. */
    isNewAssignment: boolean;
  }>;
  /** Saved roles to delete, after the writes above. */
  deletes: Assignment[];
}

/** The employee ids a role currently has saved. */
export function baselineIds(assignment: Assignment): Set<string> {
  return new Set(assignment.employees.map((e) => e.id));
}

export function setsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const x of a) if (!b.has(x)) return false;
  return true;
}

/** The selection in force for a role: the pending pick, else what's saved. */
export function selectionFor(
  assignment: Assignment,
  selections: SelectionMap,
): Set<string> {
  return selections.get(assignment.id) ?? baselineIds(assignment);
}

/**
 * Whether a role's assignees differ from what's saved. A role queued for deletion
 * is never "dirty" on its own account — the delete is the change.
 */
export function isSelectionDirty(
  assignment: Assignment,
  selections: SelectionMap,
): boolean {
  const pending = selections.get(assignment.id);
  return pending ? !setsEqual(pending, baselineIds(assignment)) : false;
}

/** Roles whose tabs are still live: everything not queued for deletion. */
export function activeTabs(state: PendingState): Assignment[] {
  return state.assignments.filter((a) => !state.pendingDeleteIds.has(a.id));
}

/**
 * Is there anything to save? Structural changes count on their own, so "Save All"
 * is enabled when the only pending change is an added or deleted role.
 */
export function hasPendingChanges(state: PendingState): boolean {
  if (state.pendingAddIds.size > 0 || state.pendingDeleteIds.size > 0) return true;
  return state.assignments.some((a) => isSelectionDirty(a, state.selections));
}

/**
 * Where focus should sit. Picking people for a role you've queued for deletion is
 * meaningless, so focus leaves it for the first live tab; with none left the
 * caller shows the empty state.
 */
export function nextActiveId(state: PendingState, currentId: string): string {
  const live = activeTabs(state);
  if (live.some((a) => a.id === currentId)) return currentId;
  return live[0]?.id ?? "";
}

/**
 * The people already committed elsewhere on this project, who therefore can't be
 * picked for the active role. Roles queued for deletion are excluded: freeing
 * someone up by deleting their role and moving them is the whole point of doing
 * both in one modal.
 */
export function assignedElsewhereIds(
  state: PendingState,
  activeAssignmentId: string | null,
): string[] {
  const ids: string[] = [];
  for (const a of state.assignments) {
    if (a.id === activeAssignmentId) continue;
    if (state.pendingDeleteIds.has(a.id)) continue;
    for (const id of selectionFor(a, state.selections)) ids.push(id);
  }
  return ids.sort();
}

/**
 * Turn the pending state into the writes a save performs, in order: inserts,
 * then employee attachments, then deletes. Roles queued for deletion contribute
 * no employee write — their assignees go away with the row.
 */
export function buildSavePlan(state: PendingState): SavePlan {
  const inserts: Assignment[] = [];
  const employeeWrites: SavePlan["employeeWrites"] = [];
  const deletes: Assignment[] = [];

  for (const assignment of state.assignments) {
    if (state.pendingDeleteIds.has(assignment.id)) {
      deletes.push(assignment);
      continue;
    }

    const isNewAssignment = state.pendingAddIds.has(assignment.id);
    if (isNewAssignment) inserts.push(assignment);

    const selectedIds = selectionFor(assignment, state.selections);
    // A brand-new role with nobody on it needs no employee write at all.
    if (isNewAssignment ? selectedIds.size > 0 : isSelectionDirty(assignment, state.selections)) {
      employeeWrites.push({ assignment, selectedIds, isNewAssignment });
    }
  }

  return { inserts, employeeWrites, deletes };
}

/** The one optimistic board patch a fully successful save applies. */
export type BoardUpdate =
  | { kind: "structure"; projectAssignments: Assignment[] }
  | {
      kind: "employees";
      updates: EmployeeBoardUpdate[];
    };

/** One assignment's new employees, plus its saved lock when the save reported it. */
export interface EmployeeBoardUpdate {
  assignmentId: string;
  employees: AssignmentEmployee[];
  isConfirmed?: boolean | null;
}

/**
 * Decide the single optimistic board update for a fully successful save.
 *
 * When roles were added or removed, the board gets the new role list with the
 * written assignees merged in, as one patch. The employee-only update must not
 * follow it: that updater rebuilds `project_assignments` from the project
 * snapshot captured when the modal opened, which would drop the new roles and
 * restore the deleted ones. With the structure unchanged, the employee-only
 * update is enough. Returns null when there is nothing to patch.
 */
export function buildBoardUpdate(
  state: PendingState,
  plan: SavePlan,
  writtenEmployees: Map<string, AssignmentEmployee[]>,
  /** is_confirmed as each save returned it; overrides this client's copy. */
  persistedConfirmed: Map<string, boolean | null> = new Map(),
): BoardUpdate | null {
  if (plan.inserts.length > 0 || plan.deletes.length > 0) {
    return {
      kind: "structure",
      projectAssignments: state.assignments
        .filter((a) => !state.pendingDeleteIds.has(a.id))
        .map((a) =>
          writtenEmployees.has(a.id)
            ? withWrittenEmployees(
                a,
                writtenEmployees.get(a.id)!,
                persistedConfirmed.get(a.id),
              )
            : a,
        ),
    };
  }
  if (writtenEmployees.size > 0) {
    return {
      kind: "employees",
      updates: [...writtenEmployees].map(([assignmentId, employees]) => ({
        assignmentId,
        employees,
        isConfirmed: persistedConfirmed.get(assignmentId),
      })),
    };
  }
  return null;
}
